/**
 * Food Log Hook - Daily food tracking with macro totals
 * 
 * Manages food entries for a selected date with backend sync.
 * Similar pattern to useMealPlan for consistency.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFoodLog,
  addFoodEntry as apiAddEntry,
  updateFoodEntry as apiUpdateEntry,
  deleteFoodEntry as apiDeleteEntry,
  getMacroPreferences,
  updateMacroPreferences as apiUpdatePreferences,
  getToday,
  DEFAULT_TARGETS
} from '../utils/foodLogApi';

export default function useFoodLog(getToken, isAuthenticated) {
  // ============ STATE ============

  const [selectedDate, setSelectedDate] = useState(() => getToday());
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Macro preferences
  const [trackedMacros, setTrackedMacros] = useState(['protein', 'carbs', 'fat']);
  const [dailyTargets, setDailyTargets] = useState(DEFAULT_TARGETS);
  const [availableMacros, setAvailableMacros] = useState([]);

  // Track current date to detect changes
  const currentDateRef = useRef(selectedDate);
  const initialLoadDone = useRef(false);

  // ============ LOAD FOOD LOG ============

  const loadFoodLog = useCallback(async (date = selectedDate) => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = await getToken();
      const result = await getFoodLog(date, token);

      if (result.success) {
        setEntries(result.entries || []);
        setTotals(result.totals || {});
      }
    } catch (err) {
      console.error('Failed to load food log:', err);
    } finally {
      setIsLoading(false);
      initialLoadDone.current = true;
    }
  }, [isAuthenticated, getToken, selectedDate]);

  // Load on mount and when date changes
  useEffect(() => {
    if (currentDateRef.current !== selectedDate || !initialLoadDone.current) {
      currentDateRef.current = selectedDate;
      loadFoodLog(selectedDate);
    }
  }, [selectedDate, loadFoodLog]);

  // ============ LOAD PREFERENCES ============

  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = await getToken();
      const result = await getMacroPreferences(token);

      if (result.success) {
        setTrackedMacros(result.preferences.trackedMacros || ['protein', 'carbs', 'fat']);
        setDailyTargets({ ...DEFAULT_TARGETS, ...result.preferences.dailyTargets });
        setAvailableMacros(result.availableMacros || []);
      }
    } catch (err) {
      console.error('Failed to load macro preferences:', err);
    }
  }, [isAuthenticated, getToken]);

  // Load preferences on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadPreferences();
    }
  }, [isAuthenticated, loadPreferences]);

  // ============ HANDLERS ============

  // Add a food entry
  const addEntry = useCallback(async (entry) => {
    if (!isAuthenticated) return null;

    try {
      setIsSaving(true);
      const token = await getToken();
      
      const result = await apiAddEntry({
        ...entry,
        date: entry.date || selectedDate
      }, token);

      if (result.success && result.entry) {
        // Update local state
        setEntries(prev => [...prev, result.entry]);
        
        // Update totals
        const macros = result.entry.macros || {};
        setTotals(prev => {
          const newTotals = { ...prev };
          Object.keys(macros).forEach(key => {
            newTotals[key] = (newTotals[key] || 0) + macros[key];
          });
          return newTotals;
        });

        return result.entry;
      }
    } catch (err) {
      console.error('Failed to add food entry:', err);
    } finally {
      setIsSaving(false);
    }
    return null;
  }, [isAuthenticated, getToken, selectedDate]);

  // Update a food entry
  const updateEntry = useCallback(async (id, updates) => {
    if (!isAuthenticated) return false;

    try {
      setIsSaving(true);
      const token = await getToken();
      const result = await apiUpdateEntry(id, updates, token);

      if (result.success && result.entry) {
        // Reload to get accurate totals
        await loadFoodLog(selectedDate);
        return true;
      }
    } catch (err) {
      console.error('Failed to update food entry:', err);
    } finally {
      setIsSaving(false);
    }
    return false;
  }, [isAuthenticated, getToken, selectedDate, loadFoodLog]);

  // Delete a food entry
  const deleteEntry = useCallback(async (id) => {
    if (!isAuthenticated) return false;

    try {
      setIsSaving(true);
      const token = await getToken();
      
      // Find entry to subtract from totals
      const entry = entries.find(e => e.id === id);
      
      const result = await apiDeleteEntry(id, token);

      if (result.success) {
        // Update local state
        setEntries(prev => prev.filter(e => e.id !== id));
        
        // Update totals
        if (entry?.macros) {
          setTotals(prev => {
            const newTotals = { ...prev };
            Object.keys(entry.macros).forEach(key => {
              newTotals[key] = Math.max(0, (newTotals[key] || 0) - entry.macros[key]);
            });
            return newTotals;
          });
        }

        return true;
      }
    } catch (err) {
      console.error('Failed to delete food entry:', err);
    } finally {
      setIsSaving(false);
    }
    return false;
  }, [isAuthenticated, getToken, entries]);

  // Update macro preferences
  const updatePreferences = useCallback(async (preferences) => {
    if (!isAuthenticated) return false;

    try {
      const token = await getToken();
      const result = await apiUpdatePreferences(preferences, token);

      if (result.success) {
        if (preferences.trackedMacros) {
          setTrackedMacros(preferences.trackedMacros);
        }
        if (preferences.dailyTargets) {
          setDailyTargets(prev => ({ ...prev, ...preferences.dailyTargets }));
        }
        return true;
      }
    } catch (err) {
      console.error('Failed to update preferences:', err);
    }
    return false;
  }, [isAuthenticated, getToken]);

  // Add a macro to track
  const addTrackedMacro = useCallback(async (macro) => {
    if (trackedMacros.includes(macro)) return true;
    const newTracked = [...trackedMacros, macro];
    return updatePreferences({ trackedMacros: newTracked });
  }, [trackedMacros, updatePreferences]);

  // Remove a macro from tracking
  const removeTrackedMacro = useCallback(async (macro) => {
    const newTracked = trackedMacros.filter(m => m !== macro);
    if (newTracked.length === 0) return false; // Keep at least one
    return updatePreferences({ trackedMacros: newTracked });
  }, [trackedMacros, updatePreferences]);

  // Update a daily target
  const updateDailyTarget = useCallback(async (macro, target) => {
    return updatePreferences({ dailyTargets: { [macro]: target } });
  }, [updatePreferences]);

  // Navigate to different date
  const changeDate = useCallback((newDate) => {
    setSelectedDate(newDate);
  }, []);

  // Go to today
  const goToToday = useCallback(() => {
    setSelectedDate(getToday());
  }, []);

  // ============ COMPUTED VALUES ============

  // Group entries by meal type
  const entriesByMeal = {
    breakfast: entries.filter(e => e.mealType === 'breakfast'),
    lunch: entries.filter(e => e.mealType === 'lunch'),
    dinner: entries.filter(e => e.mealType === 'dinner'),
    snack: entries.filter(e => e.mealType === 'snack')
  };

  // Check if selected date is today
  const isToday = selectedDate === getToday();

  // ============ RETURN ============

  return {
    // State
    selectedDate,
    entries,
    entriesByMeal,
    totals,
    isLoading,
    isSaving,
    isToday,

    // Preferences
    trackedMacros,
    dailyTargets,
    availableMacros,

    // Actions
    addEntry,
    updateEntry,
    deleteEntry,
    changeDate,
    goToToday,
    loadFoodLog,

    // Preference actions
    addTrackedMacro,
    removeTrackedMacro,
    updateDailyTarget,
    updatePreferences
  };
}
